import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { clearCorporateSession, getCorporateToken } from "../utils/corporateSession";

const CorporateRejected = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMe = async () => {
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        setMe(res.data.data);
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        clearCorporateSession();
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      clearCorporateSession();
      navigate("/corporate/login", { replace: true });
    }
  };

  const fetchRejected = async (pageNum = 1) => {
    setRefreshing(true);
    try {
      const res = await api.get(`/corporate/bookings?status=cancelled&page=${pageNum}&limit=${limit}`);
      if (res?.data?.success) {
        setBookings(res.data.data || []);
        setPagination(res.data.pagination);
        setPage(pageNum);
      } else {
        toast.error(res?.data?.message || "Failed to load bookings");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load bookings");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const token = getCorporateToken();
    if (!token) {
      navigate("/corporate/login", { replace: true });
      return;
    }

    (async () => {
      setLoading(true);
      await fetchMe();
      setLoading(false);
    })();
  }, [navigate]);

  useEffect(() => {
    if (me?.role === "admin") {
      fetchRejected();
    }
  }, [me?.role]);

  const getDisplayBookingId = (b) => {
    if (!b) return "";
    const idVal = b.bookingId || b._id;
    if (idVal && typeof idVal === "object") {
      return idVal.bookingId || idVal._id || String(idVal);
    }
    return String(idVal || "");
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 py-12">
        <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm font-medium text-slate-500">Loading rejected history...</p>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="max-w-md mx-auto my-12 text-center p-8 bg-white border border-slate-100 shadow-sm rounded-2xl">
        <div className="w-16 h-16 mx-auto bg-amber-50 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-800">Session Expired</h2>
        <p className="text-sm text-slate-500 mt-2">Please login to your corporate account again.</p>
        <button
          onClick={() => navigate("/corporate/login")}
          className="mt-6 w-full py-2.5 px-4 bg-[#F56753] hover:bg-[#d94e3a] text-white rounded-xl font-medium shadow-sm transition-all"
        >
          Login Now
        </button>
      </div>
    );
  }

  if (me.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto my-12 p-8 bg-white border border-slate-100 shadow-sm rounded-2xl">
        <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mb-5">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800">Not Authorized</h2>
        <p className="text-sm text-slate-500 mt-2">Only corporate administrators can inspect rejected travel bookings records.</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-white border border-slate-100 rounded-2xl shadow-sm">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-rose-600 bg-rose-50/60 px-3 py-1 rounded-full w-max mb-2">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Review Rejected
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">Rejected Bookings</h1>
          <p className="text-sm text-slate-500 mt-1">Detailed list of employee rides rejected or cancelled with moderation details.</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchRejected(page)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold shadow-sm transition-all disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <span className="w-2.5 h-2.5 bg-rose-500 rounded-full animate-ping"></span>
            <h2 className="text-base font-bold text-slate-800">Rejected Travel Registry</h2>
          </div>
          {pagination && (
            <span className="text-xs font-semibold text-rose-800 bg-rose-50 px-3 py-1 rounded-full">
              {pagination.totalCount} {pagination.totalCount === 1 ? "Record" : "Records"}
            </span>
          )}
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 mx-auto bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-800">No rejected records</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Travel bookings that were rejected or cancelled will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/30">
                  <th className="px-6 py-4">Booking Info</th>
                  <th className="px-6 py-4">Route Details</th>
                  <th className="px-6 py-4">Pickup Date</th>
                  <th className="px-6 py-4">Requested By</th>
                  <th className="px-6 py-4 text-right">Fare</th>
                  <th className="px-6 py-4">Rejection Reason</th>
                  <th className="px-6 py-4 text-right">Rejected At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bookings.map((b) => {
                  const displayId = getDisplayBookingId(b);
                  return (
                    <tr key={b._id} className="hover:bg-slate-50/40 transition-colors">
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <span className="inline-block font-mono text-[11px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
                            {displayId}
                          </span>
                          <div className="text-[11px] font-semibold text-[#F56753] bg-red-50 px-2 py-0.5 rounded w-max">
                            {b.cabType?.name || "Standard Cab"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1.5 max-w-[280px]">
                          <div className="flex items-start gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0"></span>
                            <span className="text-xs font-bold text-slate-800 truncate" title={b.pickupLocation?.name || b.from}>
                              {b.pickupLocation?.name || b.from || "-"}
                            </span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="w-2 h-2 rounded-full bg-rose-500 mt-1.5 shrink-0"></span>
                            <span className="text-xs text-slate-500 truncate" title={b.dropLocation?.name || b.to}>
                              {b.dropLocation?.name || b.to || "-"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-800">
                          {b.pickupDate ? new Date(b.pickupDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          {b.pickupTime || ""}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={b.corporateMemberId?.profileImageUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                            alt="Staff"
                            className="w-9 h-9 rounded-full object-cover border border-slate-200 shadow-sm"
                            onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"; }}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-800 tracking-tight">
                              {b.corporateMemberId?.fullName || "Employee"}
                            </span>
                            <span className="text-[11px] text-slate-500">
                              {b.corporateMemberId?.email || ""}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="font-extrabold text-slate-900 text-base">
                          ₹{Number(b.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-[180px] bg-rose-50/50 border border-rose-100 rounded-lg p-2 text-xs font-semibold text-rose-700 italic leading-snug">
                          {b.cancellationReason || "Declined by administrator"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="font-semibold text-slate-700">
                          {b.statusChangedAt ? new Date(b.statusChangedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {b.statusChangedAt ? new Date(b.statusChangedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Sleek Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <button
              onClick={() => fetchRejected(page - 1)}
              disabled={!pagination.hasPreviousPage || refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-100 text-slate-700 rounded-lg text-xs font-bold transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Previous
            </button>

            <span className="text-xs font-semibold text-slate-500">
              Page {page} of {pagination.totalPages}
            </span>

            <button
              onClick={() => fetchRejected(page + 1)}
              disabled={!pagination.hasNextPage || refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-100 text-slate-700 rounded-lg text-xs font-bold transition-all"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateRejected;
